/**
 * Reader for `SessionState._meta["copilot.checkpoints"]`, the only place the
 * host reports a `/compact` result.
 *
 * Contract: github/copilot-host
 * `spec/meta-conventions/copilot/schemas/session-meta.copilot.checkpoints.schema.json`
 */

const META_KEY_SESSION_CHECKPOINTS = 'copilot.checkpoints'

export const CHECKPOINT_PHASE_COMPLETE = 'complete' as const

/** One checkpoint entry. Entries without `phase` are catalogue rows, not compactions. */
export interface RelayCheckpoint {
  checkpointNumber: number
  /** Absent on a live-only entry until the catalogue catches up. */
  title?: string
  phase?: typeof CHECKPOINT_PHASE_COMPLETE
  success?: boolean
  tokensRemoved?: number
  messagesRemoved?: number
  error?: string
}

export type CheckpointAnchors = ReadonlyMap<number, string | null>

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined
}

function count(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function readCheckpoint(value: unknown): RelayCheckpoint | undefined {
  const entry = record(value)
  const checkpointNumber = count(entry?.['checkpointNumber'])
  if (!entry || checkpointNumber === undefined) return undefined

  const checkpoint: RelayCheckpoint = {checkpointNumber}
  const title = text(entry['title'])
  if (title !== undefined) checkpoint.title = title
  if (entry['phase'] === CHECKPOINT_PHASE_COMPLETE) checkpoint.phase = CHECKPOINT_PHASE_COMPLETE
  if (typeof entry['success'] === 'boolean') checkpoint.success = entry['success']
  const tokensRemoved = count(entry['tokensRemoved'])
  if (tokensRemoved !== undefined) checkpoint.tokensRemoved = tokensRemoved
  const messagesRemoved = count(entry['messagesRemoved'])
  if (messagesRemoved !== undefined) checkpoint.messagesRemoved = messagesRemoved
  // `error` is untyped in the contract: accept a string or an Error-shaped object.
  const error = text(entry['error']) ?? text(record(entry['error'])?.['message'])
  if (error !== undefined) checkpoint.error = error
  return checkpoint
}

/**
 * The checkpoint entries, or `undefined` when the host omits the key (it does
 * not support checkpoints). Malformed fields are dropped; entries without a
 * `checkpointNumber` are skipped.
 */
export function readCheckpoints(meta: Record<string, unknown> | undefined): readonly RelayCheckpoint[] | undefined {
  const value = meta?.[META_KEY_SESSION_CHECKPOINTS]
  if (!Array.isArray(value)) return undefined

  const checkpoints: RelayCheckpoint[] = []
  for (const item of value) {
    const checkpoint = readCheckpoint(item)
    if (checkpoint) checkpoints.push(checkpoint)
  }
  return checkpoints
}

/** Completed compactions, oldest first. Catalogue rows (no `phase`) are skipped. */
export function completedCheckpoints(checkpoints: readonly RelayCheckpoint[] | undefined): readonly RelayCheckpoint[] {
  return (checkpoints ?? [])
    .filter(checkpoint => checkpoint.phase === CHECKPOINT_PHASE_COMPLETE)
    .sort((a, b) => a.checkpointNumber - b.checkpointNumber)
}

export function updateCheckpointAnchors(
  anchors: CheckpointAnchors,
  checkpoints: readonly RelayCheckpoint[] | undefined,
  resolveAnchor: (checkpointNumber: number, anchors: CheckpointAnchors) => string | null | undefined,
): CheckpointAnchors {
  if (checkpoints === undefined) return anchors
  const completed = completedCheckpoints(checkpoints)
  const numbers = new Set(completed.map(checkpoint => checkpoint.checkpointNumber))
  let next: Map<number, string | null> | undefined
  for (const number of anchors.keys()) {
    if (numbers.has(number)) continue
    next ??= new Map(anchors)
    next.delete(number)
  }
  for (const {checkpointNumber} of completed) {
    if ((next ?? anchors).has(checkpointNumber)) continue
    const anchor = resolveAnchor(checkpointNumber, next ?? anchors)
    if (anchor === undefined) continue
    next ??= new Map(anchors)
    next.set(checkpointNumber, anchor)
  }
  return next ?? anchors
}
